import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Input } from '@components/ui';

const DEFAULT_COLORS = [
  '#667eea', // Indigo
  '#10b981', // Emerald
  '#f59e0b', // Amber
  '#ef4444', // Red
  '#8b5cf6', // Violet
  '#06b6d4', // Cyan
  '#ec4899', // Pink
  '#84cc16', // Lime
];

export default function EpicModal({
  isOpen,
  onClose,
  onSubmit,
  epic = null,
  isLoading = false,
}) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#667eea',
    startWeek: '',
    duration: '',
  });

  useEffect(() => {
    if (epic) {
      setFormData({
        name: epic.name || '',
        description: epic.description || '',
        color: epic.color || '#667eea',
        startWeek: epic.startWeek || '',
        duration: epic.duration || '',
      });
    } else {
      setFormData({
        name: '',
        description: '',
        color: '#667eea',
        startWeek: '',
        duration: '',
      });
    }
  }, [epic, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleColorSelect = (color) => {
    setFormData((prev) => ({ ...prev, color }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      startWeek: formData.startWeek ? parseInt(formData.startWeek) : null,
      duration: formData.duration ? parseInt(formData.duration) : null,
    });
  };

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#475569',
    marginBottom: '6px',
  };

  const textareaStyle = {
    width: '100%',
    padding: '10px 16px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '14px',
    color: '#1e293b',
    resize: 'none',
    outline: 'none',
    fontFamily: 'inherit',
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={epic ? 'Editar Épica' : 'Nueva Épica'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={isLoading}>
            {epic ? 'Guardar' : 'Crear Épica'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Input
            label="Nombre de la Épica"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Ej: Sistema de Autenticación"
            required
          />

          <div>
            <label style={labelStyle}>Descripción</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe el objetivo de esta épica..."
              rows={3}
              style={textareaStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Color</label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {DEFAULT_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => handleColorSelect(color)}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    backgroundColor: color,
                    border: formData.color === color ? '3px solid #1e293b' : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'transform 0.15s',
                    transform: formData.color === color ? 'scale(1.1)' : 'scale(1)',
                  }}
                  title={color}
                />
              ))}
              <input
                type="color"
                value={formData.color}
                onChange={(e) => handleColorSelect(e.target.value)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  border: '2px solid #e2e8f0',
                  cursor: 'pointer',
                  padding: 0,
                }}
                title="Color personalizado"
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <Input
              label="Semana de Inicio"
              name="startWeek"
              type="number"
              min="0"
              max="52"
              value={formData.startWeek}
              onChange={handleChange}
              placeholder="0"
            />

            <Input
              label="Duración (semanas)"
              name="duration"
              type="number"
              min="1"
              max="52"
              value={formData.duration}
              onChange={handleChange}
              placeholder="4"
            />
          </div>

          <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>
            La semana de inicio y duración se utilizan para mostrar la épica en el timeline del Roadmap.
          </p>
        </div>
      </form>
    </Modal>
  );
}

EpicModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  epic: PropTypes.object,
  isLoading: PropTypes.bool,
};
